import { cn } from "@utils";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ProfilePaymentIcons } from "@components";

const toneStyles = {
  friendly: "text-green-600 border-green-300 bg-green-50",
  firm: "text-orange-600 border-orange-300 bg-orange-50",
  urgent: "text-red-600 border-red-300 bg-red-50",
};

const invoiceLabels = {
  None: "No invoices requested",
  Overdue: "Overdue",
  OverdueAndDueThisMonth: "Overdue + Due",
  All: "All invoices",
  SpecificInvoices: "Specific invoices",
};

function ToneBadge({ tone }) {
  const label = tone.charAt(0).toUpperCase() + tone.slice(1);
  return (
    <span
      className={`text-xs font-medium px-2.5 py-0.5 rounded-md border ${toneStyles[tone] ?? "text-zinc-600 border-zinc-300 bg-zinc-50"}`}
    >
      {label}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span className="text-xs font-semibold text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-full px-4 py-2 inline-flex items-center">
      {children}
    </span>
  );
}

function buildTags(template, paymentProfiles) {
  const tags = [];
  if (template.invoicesRequested == null) {
    tags.push("Custom amount");
  } else {
    const invoiceLabel = invoiceLabels[template.invoicesRequested];
    if (invoiceLabel) {
      const isMandatory = template.paymentRequirement === "Mandatory";
      tags.push(invoiceLabel + (isMandatory ? " · Mandatory" : ""));
    }
  }
  if (template.attachPdfStatement) {
    tags.push("PDF Statement");
  }
  if (template.allowCardPayment && !paymentProfiles) {
    tags.push("Card payments");
  }
  const channels = (template.messages || []).map((m) => m.communicationMethod);
  if (channels.includes("Email") && channels.includes("Sms")) {
    tags.push("Email + SMS");
  } else if (channels.includes("Email")) {
    tags.push("Email");
  } else if (channels.includes("Sms")) {
    tags.push("SMS");
  }
  if (paymentProfiles && template.paymentProfileId != null) {
    const profile = paymentProfiles.find(
      (p) => p.id === template.paymentProfileId,
    );
    if (profile) {
      tags.push(
        <span className="inline-flex items-center gap-1.5">
          {profile.name}
          <ProfilePaymentIcons profile={profile} size="sm" />
        </span>,
      );
    }
  }
  return tags;
}

function TemplateTile({
  template,
  onClick,
  onMenuClick,
  isSelected,
  disabled,
  paymentProfiles,
}) {
  const tags = buildTags(template, paymentProfiles);
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick?.(template)}
      disabled={disabled}
      className={cn(
        "group w-full flex flex-col items-start rounded-xl border bg-white p-5 shadow-sm transition-all duration-150",
        disabled
          ? "border-zinc-200 opacity-50"
          : isSelected
            ? "border-primary-500 ring-2 ring-primary-500"
            : "border-zinc-200 hover:border-zinc-300 hover:shadow-md",
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex items-center gap-2">
          <h3 className="text-[15px] font-semibold text-zinc-900">
            {template.name}
          </h3>
          {onMenuClick && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick(template);
              }}
              className="p-1 flex items-center justify-center text-primary shrink-0 hover:text-primary-700 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <PencilSquareIcon className="size-5" />
            </button>
          )}
          {template.description && (
            <p className="text-sm text-zinc-500 mt-0.5">
              {template.description}
            </p>
          )}
        </div>
        {template.tone && <ToneBadge tone={template.tone} />}
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {tags.map((tag, i) => (
            <Tag key={i}>{tag}</Tag>
          ))}
        </div>
      )}
    </button>
  );
}

export default TemplateTile;
export { Tag, buildTags, invoiceLabels };
